import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TablerIconsModule } from 'angular-tabler-icons';
import * as constants from 'src/app/common/constant/constants.type';
import { MedicalReportType, MedicalReportTypeLabels } from 'src/app/common/enums/medical-report-type';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { MaterialModule } from 'src/app/material.module';
import { environment } from 'src/environments/environment';
import { MedicalRecordDetailComponent } from './medical-record-detail/medical-record-detail.component';
import { MedicalRecordService } from './medical.service';
import { IMedicalRecord } from './medical.type';
import { HttpHeaders } from '@angular/common/http';

@Component({
  selector: 'app-medical-record',
  imports: [CommonModule, MaterialModule, TablerIconsModule],
  providers: [DatePipe],
  templateUrl: './medical-record.component.html',
  styleUrl: './medical-record.component.scss',
})
export class MedicalRecordComponent implements OnInit, OnDestroy {
  @Input() patientId!: string | number | undefined;
  @Output() medicalRecordLength = new EventEmitter<number>();

  medicalRecords: IMedicalRecord[] = [];

  readonly medicalReportType = MedicalReportType;
  readonly medicalReportTypeLabels = MedicalReportTypeLabels;

  private destroy$ = new Subject<void>();

  constructor(
    public dialog: MatDialog,
    private medicalRecordService: MedicalRecordService,
    private datePipe: DatePipe,
    private sanitizer: DomSanitizer,
    private toastr: ToastrService,
    private translationUtils: TranslationUtils,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.patientId && this.loadMedicalRecords();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDownload(record: IMedicalRecord): void {
    this.medicalRecordService.downloadMedicalRecord(record.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ body, headers }) => {
        if (!body) return;

        const fileName = this.extractFileName(headers, record.fileName);
        this.triggerDownload(body, fileName);
      });
  }

  private extractFileName(headers: HttpHeaders, fallback = 'medical-record'): string {
    const contentDisposition = headers.get('content-disposition') || '';
    const utf8 = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    const normal = contentDisposition.match(/filename="?([^"]+)"?/i)?.[1];

    return decodeURIComponent(utf8 || normal || fallback);
  }

  private triggerDownload(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();

    URL.revokeObjectURL(url);
  }

  openPreview(record: IMedicalRecord): void {
    record.fileUrl && window.open(record.fileUrl, '_blank', 'noopener,noreferrer');
  }

  onPreviewLoadError(record: IMedicalRecord): void {
    record.previewType = 'file';
    record.previewUrl = '';
  }

  onDelete(record: IMedicalRecord): void {
    this.medicalRecordService.deleteMedicalRecord(record.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (!res.isSuccess) return;

        this.toastr.success(
          this.translationUtils.getTranslatedMessage(
            'COMMON.DELETE',
            this.translate.instant('ENTITY.RECORD')
          )
        );
        this.loadMedicalRecords();
      });
  }

  onUpload(): void {
    this.dialog.open(MedicalRecordDetailComponent, {
      width: '100vw',
      maxWidth: '600px',
      disableClose: true,
      data: { patientId: this.patientId }
    })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => res && this.loadMedicalRecords());
  }

  loadMedicalRecords(): void {
    this.medicalRecordService.getMedicalRecordByPatientID(Number(this.patientId))
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (!res.isSuccess || !res.result) {
          this.resetRecords();
          return;
        }

        this.medicalRecords = res.result.map(this.mapRecord.bind(this));
        this.medicalRecordLength.emit(this.medicalRecords.length);
      });
  }

  private resetRecords(): void {
    this.medicalRecords = [];
    this.medicalRecordLength.emit(0);
  }

  private mapRecord(record: IMedicalRecord): IMedicalRecord {
    const fileName = this.getFileName(record.filePath);
    const fileUrl = this.buildFileUrl(record.filePath);
    const previewType = this.getPreviewType(fileName);

    return {
      ...record,
      description: record.description || 'Medical Record',
      uploadDate: this.datePipe.transform(record.uploadDate, constants.DDMMYYYY_FORMAT) as string,
      fileName,
      fileUrl,
      previewType,
      previewUrl: this.buildPreviewUrl(fileUrl, previewType)
    };
  }

  getRecordTypeLabel(type: MedicalReportType): string {
    return this.medicalReportTypeLabels[type] ?? String(type);
  }

  getFileBadgeLabel(record: IMedicalRecord): string {
    return this.getFileExtension(record.fileName || '')?.toUpperCase() || 'FILE';
  }

  private getFileName(path?: string): string {
    return path?.replace(/\\/g, '/').split('/').pop() || 'medical-record';
  }

  private getFileExtension(fileName: string): string {
    return fileName.split('.').pop()?.toLowerCase() || '';
  }

  private getPreviewType(fileName: string): IMedicalRecord['previewType'] {
    const ext = this.getFileExtension(fileName);

    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';

    return 'file';
  }

  private buildFileUrl(path?: string): string {
    return path ? `${environment.imagePath}${path.replace(/^\/+/, '')}` : '';
  }

  private buildPreviewUrl(fileUrl: string, type: IMedicalRecord['previewType']) {
    return type === 'pdf'
      ? this.sanitizer.bypassSecurityTrustResourceUrl(fileUrl)
      : fileUrl;
  }
}
